// @catalog-federation — record types, identifiers and published shapes
// ============================================================================
// The TypeScript twin of LedgerRecordTypes.cs, OperatorCode.cs and the record
// bodies of LedgerRecordBody.cs / LedgerRecordBodies.cs (docs/cid-governance.md
// §4.2). The body interfaces below are the published JSON, field for field.
// They describe; they do not validate: the consumers that apply records (CPR,
// CID, EFS) validate every body in C#, and no TypeScript code applies one. The
// one exception is the keyless service listing (apps/docs/plans/operator-codes.md
// §5): its rules are pinned in both runtimes, like the operator-code rule, by
// readOperatorServicesBody below.

import { StrictObject } from './json';

// ── record types and service kinds ──────────────────────────────────────

/** The `type` of a ledger envelope. It is inside the signed bytes, so no body
 *  of one type can be replayed as another. `protected_organization` is decided
 *  after M11 and absent until then (stored as `unknown_type`). */
export const LedgerRecordTypes = {
    OperatorReservation: 'operator_reservation',
    OperatorBinding: 'operator_binding',
    OperatorBindingRelease: 'operator_binding_release',
    OperatorRetirement: 'operator_retirement',
    ServiceCertificate: 'service_certificate',
    ServiceCertificateEnd: 'service_certificate_end',
    AssetIdAllocation: 'assetid_allocation',
    UsernameBatch: 'username_batch',
    AccountOriginMigration: 'account_origin_migration',
    OriginMigrationBulk: 'origin_migration_bulk',
    BitcashAdministratorCertificate: 'bitcash_administrator_certificate',
    OperatorServices: 'operator_services',
} as const;

export type LedgerRecordType = (typeof LedgerRecordTypes)[keyof typeof LedgerRecordTypes];

export const LEDGER_RECORD_TYPES: readonly LedgerRecordType[] = Object.values(LedgerRecordTypes);

export function isKnownRecordType(type: unknown): type is LedgerRecordType {
    return typeof type === 'string' && (LEDGER_RECORD_TYPES as readonly string[]).includes(type);
}

/** Kinds of a `service_certificate` (D7). `relay` and `la` are reserved: the
 *  slot exists, the key schema does not. */
export const ServiceKinds = {
    Cpr: 'cpr',
    Cid: 'cid',
    Relay: 'relay',
    La: 'la',
} as const;

export type DefinedServiceKind = typeof ServiceKinds.Cpr | typeof ServiceKinds.Cid;

export function isDefinedServiceKind(kind: unknown): kind is DefinedServiceKind {
    return kind === ServiceKinds.Cpr || kind === ServiceKinds.Cid;
}

/** The keys a certificate names: `cpr.block`; `cid.sync` and `cid.jwt`. */
export const CertifiedKeyNames = {
    /** `cpr.block` — the former CPR operator key. */
    Block: 'block',
    /** `cid.sync` — the CID node signing key: op log, sync handshake, revocation feed, claim review. */
    Sync: 'sync',
    /** `cid.jwt` — the OIDC token key (ES256), with the tokens' `kid`. */
    Jwt: 'jwt',
} as const;

/** The two forms of an origin migration (D20). */
export const MigrationModes = {
    Voluntary: 'voluntary',
    Forced: 'forced',
} as const;

// ── identifiers ─────────────────────────────────────────────────────────

/** The operator code (apps/docs/plans/operator-codes.md §3): two to four
 *  lowercase ASCII letters or digits. No restricted alphabet, no look-alike
 *  check: the Developer picks every code by hand. Never the Bitcash ledger's
 *  loose operator grammar. Pinned with C# `OperatorCode` by
 *  libs/federation/vectors/operator-codes.json. */
const OPERATOR_CODE_RE = /^[a-z0-9]{2,4}$/;

export function isOperatorCode(code: unknown): code is string {
    return typeof code === 'string' && OPERATOR_CODE_RE.test(code);
}

/** Codes of a valid shape that can never be reserved (§3.2): subdomains used
 *  or that may be used under catalog.org, catalog.id, bit.cash and bit.pub, and
 *  generic infrastructure labels. Only where a code is created (the
 *  `operator_reservation` body, `cl-root reserve`) consults the list; shape
 *  checks never do. C# `OperatorCode.Reserved`. */
export const RESERVED_OPERATOR_CODES: readonly string[] = Object.freeze([
    'root',
    'www', 'api', 'app', 'id', 'auth', 'mail', 'mx', 'smtp', 'imap', 'pop', 'ns', 'ns1', 'ns2', 'cdn',
    'dev', 'test', 'demo', 'prod', 'live', 'stag', 'beta',
    'docs', 'repo', 'core', 'old', 'xrpl', 'help',
    'nl', 'nl6',
]);

export function isReservedOperatorCode(code: unknown): code is string {
    return typeof code === 'string' && RESERVED_OPERATOR_CODES.includes(code);
}

/** CID shapes by account type (`CidService`). The ledger checks shape only. */
const NATURAL_PERSON_CID_RE = /^[a-z]{3}[0-9]{4}[a-z]{3}$/;
const ORGANIZATION_CID_RE = /^[0-9]{3}[a-z]{4}[0-9]{3}$/;
const DELEGATE_CID_RE = /^[a-z]{2}[0-9]{2}[a-z]{2}[0-9]{2}[a-z]{2}$/;
const AGENT_CID_RE = /^[a-z]{10}$/;

export function isNaturalPersonCid(cid: unknown): cid is string {
    return typeof cid === 'string' && NATURAL_PERSON_CID_RE.test(cid);
}

export function isOrganizationCid(cid: unknown): cid is string {
    return typeof cid === 'string' && ORGANIZATION_CID_RE.test(cid);
}

export function isDelegateCid(cid: unknown): cid is string {
    return typeof cid === 'string' && DELEGATE_CID_RE.test(cid);
}

export function isAgentCid(cid: unknown): cid is string {
    return typeof cid === 'string' && AGENT_CID_RE.test(cid);
}

// ── listed services ─────────────────────────────────────────────────────

/** The services an `operator_services` listing names that this release knows
 *  (has a URL template for). A consumer ignores a name it does not know. */
export const ListedServices = {
    Efs: 'efs',
    Publisher: 'publisher',
} as const;

export const KNOWN_LISTED_SERVICES: readonly string[] = Object.freeze(Object.values(ListedServices));

/** Never listed: the kinds whose keyed certificates are authoritative. */
export const CERTIFIED_SERVICE_KINDS: readonly string[] = Object.freeze([ServiceKinds.Cid, ServiceKinds.Cpr]);

const LISTED_SERVICE_NAME_RE = /^[a-z][a-z0-9-]{1,15}$/;

/** The shape of a listed name; `cid` and `cpr` have it too. */
export function isListedServiceName(name: unknown): name is string {
    return typeof name === 'string' && LISTED_SERVICE_NAME_RE.test(name);
}

/** Null when `services` is a valid listing — every name of the shape, none
 *  certified, sorted (ordinal) and unique — else the defect. C#
 *  `ListedServices.ValidateSet`. */
export function listedServicesDefect(services: readonly string[]): string | null {
    for (let i = 0; i < services.length; i++) {
        const name = services[i]!;
        if (!isListedServiceName(name)) return `'${name}' is not a service name (^[a-z][a-z0-9-]{1,15}$).`;
        if (CERTIFIED_SERVICE_KINDS.includes(name)) return `'${name}' is never listed: its certificate is authoritative.`;
        if (i === 0) continue;
        const previous = services[i - 1]!;
        if (previous === name) return `'${name}' is listed twice.`;
        if (previous > name) return `services must be sorted: '${previous}' is listed before '${name}'.`;
    }
    return null;
}

// ── chain outcomes ──────────────────────────────────────────────────────

/** Why a ledger client stops at a record (§4.4). None is a rejection: a file
 *  the trust key did not sign, or signed for another place, is not a record. */
export const LedgerHaltReasons = {
    Gap: 'gap',
    BrokenChain: 'broken_chain',
    UnknownSigner: 'unknown_signer',
    SignerOutsideWindow: 'signer_outside_window',
    BadSignature: 'bad_signature',
    NonCanonical: 'non_canonical',
    InvalidEnvelope: 'invalid_envelope',
    ApplyFailed: 'apply_failed',
} as const;

export type LedgerHaltReason = (typeof LedgerHaltReasons)[keyof typeof LedgerHaltReasons];

/** Why a validly signed record is stored as `rejected` and skipped (D5). */
export const LedgerRejectionReasons = {
    UnknownType: 'unknown_type',
    UnknownKind: 'unknown_kind',
    InvalidBody: 'invalid_body',
} as const;

/** The ledger health payload every consumer serves under `ledger` (§4.4, D30;
 *  C# `LedgerHealth`). `halted_reason` and a rejection `reason` may also carry
 *  an applier's state code (for example `state_conflict`). */
export interface LedgerHealth {
    applied_seq: number;
    head_seq_seen: number;
    last_fetch_at: string | null;
    stale: boolean;
    halted_reason: string | null;
    rejected: { seq: number; reason: string }[];
    key_set_outdated: boolean;
}

// ── published bodies ────────────────────────────────────────────────────

export interface SigningProfileJson {
    algorithm: string;
    parameter_set: string;
}

/** One certified key; `kid` on `cid.jwt` only. */
export interface CertifiedKeyJson {
    public_key: string;
    signing_profile: SigningProfileJson;
    kid?: string;
}

/** Always sequence 1 of the operator record (D6). */
export interface OperatorReservationBody {
    jurisdiction: string;
    legal_name: string;
    operator_id: string;
    sequence: number;
}

/** The binding consent's fields plus the organization's signature over them. */
export interface OperatorBindingBody {
    effective_from: string;
    operator_id: string;
    org_cid: string;
    org_public_key: string;
    org_signature: string;
    org_signing_profile: SigningProfileJson;
    sequence: number;
}

export interface OperatorBindingReleaseBody {
    operator_id: string;
    reason: string;
    released_at: string;
    sequence: number;
}

export interface OperatorRetirementBody {
    operator_id: string;
    reason: string;
    retired_at: string;
    sequence: number;
}

export interface ServiceCertificateBody {
    active_from: string;
    keys: Record<string, CertifiedKeyJson>;
    kind: string;
    node_uuid: string;
    operator_id: string;
    sequence: number;
}

/** `last_valid` is in the kind's unit (D9): a timestamp for `cpr`, an op-log
 *  sequence for `cid`. */
export interface ServiceCertificateEndBody {
    ended_at: string;
    ends_sequence: number;
    kind: string;
    last_valid: string | number;
    node_uuid: string;
    operator_id: string;
    reason: string;
    sequence: number;
}

export interface AssetIdAllocationBody {
    allocated_at: string;
    form: 'digital' | 'physical';
    operator_id: string;
    range_end: number;
    range_start: number;
}

export interface UsernameBatchBody {
    account_type: 'natural_person' | 'organization' | 'delegate' | 'agent';
    operator_id: string;
    range_end: number;
    range_start: number;
}

/** `consent` is present exactly when `mode` is `voluntary`. */
export interface AccountOriginMigrationBody {
    account_cid: string;
    consent?: {
        account_public_key: string;
        account_signature: string;
        account_signing_profile: SigningProfileJson;
    };
    from_operator_id: string;
    mode: 'voluntary' | 'forced';
    reason: string;
    sequence: number;
    to_operator_id: string;
}

/** A standing redirect from its own ledger position on (D20). */
export interface OriginMigrationBulkBody {
    consent?: {
        org_cid: string;
        org_public_key: string;
        org_signature: string;
        org_signing_profile: SigningProfileJson;
    };
    from_operator_id: string;
    mode: 'voluntary' | 'forced';
    reason: string;
    sequence: number;
    to_operator_id: string;
}

export interface BitcashAdministratorCertificateBody {
    active_from: string;
    holder_org_cid: string;
    ledger_public_key: string;
    sequence: number;
    signing_profile: SigningProfileJson;
}

/** A Developer-signed, keyless listing of the services an operator runs
 *  beyond CID and CPR (§5.1). One chain per operator (`sequence` = previous +
 *  1); each listing replaces the whole set; `services` is sorted, unique, may
 *  be empty and never names `cid` or `cpr`. */
export interface OperatorServicesBody {
    effective_from: string;
    operator_id: string;
    sequence: number;
    services: string[];
}

/**
 * Reads an `operator_services` body by the C# validator's rules (the operator
 * code, sequence ≥ 1, the canonical timestamp, the listed-services rule, no
 * other field). Throws {@link FederationFileError} `malformed` where the C#
 * consumers store the record as `invalid_body`. Whether the operator is bound
 * and the sequence follows its last listing is a projection's question.
 */
export function readOperatorServicesBody(body: unknown): OperatorServicesBody {
    const o = StrictObject.of(body, LedgerRecordTypes.OperatorServices);
    const operatorId = o.string('operator_id');
    if (!isOperatorCode(operatorId)) {
        throw o.fail(`'operator_id' is not an operator code (two to four of a-z, 0-9): '${operatorId}'`);
    }
    const sequence = o.integer('sequence', 1);
    const services = o.strings('services');
    const defect = listedServicesDefect(services);
    if (defect !== null) throw o.fail(defect);
    const effectiveFrom = o.timestamp('effective_from');
    o.end();
    return { effective_from: effectiveFrom, operator_id: operatorId, sequence, services };
}

/** The body of each record type, as published. */
export interface LedgerRecordBodies {
    operator_reservation: OperatorReservationBody;
    operator_binding: OperatorBindingBody;
    operator_binding_release: OperatorBindingReleaseBody;
    operator_retirement: OperatorRetirementBody;
    service_certificate: ServiceCertificateBody;
    service_certificate_end: ServiceCertificateEndBody;
    assetid_allocation: AssetIdAllocationBody;
    username_batch: UsernameBatchBody;
    account_origin_migration: AccountOriginMigrationBody;
    origin_migration_bulk: OriginMigrationBulkBody;
    bitcash_administrator_certificate: BitcashAdministratorCertificateBody;
    operator_services: OperatorServicesBody;
}
